/**
 * Ably Service for MM4 Signal Reception
 * Receives real-time signals from mm4.py market making bot via Ably pub/sub
 */

class AblyService {
    constructor() {
        this.ably = null;
        this.channel = null;
        this.isConnected = false;
        this.listeners = new Map();
        this.connectionState = 'disconnected';
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.stats = {
            messagesReceived: 0,
            lastMessageTime: null,
            connectionUptime: 0
        };
    }

    /**
     * Connect to Ably and subscribe to mm4 signals channel
     * @param {string} apiKey - Ably API key
     * @param {string} channelName - Channel name (default: 'mm4-signals')
     */
    async connect(config, channelName = 'mm4-signals') {
        // Support both (apiKey, channel) and ({ apiKey, authUrl, channelName }) signatures
        let apiKey = null;
        // Use relative URL so it works on any domain (Cloudflare Pages, localhost, etc.)
        let authUrl = '/ably/token';
        let targetChannel = channelName || 'mm4-signals';

        if (typeof config === 'object' && config !== null) {
            apiKey = config.apiKey || null;
            authUrl = config.authUrl || authUrl;
            targetChannel = config.channelName || targetChannel;
        } else {
            apiKey = config || null;
        }

        if (!apiKey && !authUrl) {
            throw new Error('[Ably] API key or authUrl required');
        }

        // Prevent duplicate connections
        if (this.isConnected && this.ably) {
            // console.log('[Ably] Already connected, skipping duplicate connect()');
            return true;
        }

        // Clean up any existing connection first
        if (this.ably) {
            // console.log('[Ably] Cleaning up previous connection...');
            this.disconnect();
        }

        try {
            // console.log(`[Ably] Connecting to channel: ${targetChannel} (${apiKey ? 'key' : 'token'} auth)...`);

            // Initialize Ably Realtime client
            const clientOptions = {
                echoMessages: false,  // Don't echo our own messages
                autoConnect: true
            };
            if (apiKey) {
                clientOptions.key = apiKey;
            } else {
                clientOptions.authUrl = authUrl;
            }

            this.ably = new Ably.Realtime(clientOptions);

            // Get channel
            this.channel = this.ably.channels.get(targetChannel);

            // Subscribe to mm4 signals (SINGLE subscription only)
            this.channel.subscribe('mm4-signal', (message) => {
                this.stats.messagesReceived++;
                this.stats.lastMessageTime = Date.now();

                // Emit to all registered listeners
                this.emit('mm4-signal', message.data);
            });

            // Connection state handlers
            this.ably.connection.on('connected', () => {
                this.isConnected = true;
                this.connectionState = 'connected';
                this.reconnectAttempts = 0;
                // console.log(`[Ably] Connected to channel: ${targetChannel}`);
                this.emit('connection', { state: 'connected' });
            });

            this.ably.connection.on('disconnected', () => {
                this.isConnected = false;
                this.connectionState = 'disconnected';
                // console.log('[Ably] Disconnected');
                this.emit('connection', { state: 'disconnected' });
            });

            this.ably.connection.on('suspended', () => {
                this.isConnected = false;
                this.connectionState = 'suspended';
                // console.warn('[Ably] Connection suspended');
                this.emit('connection', { state: 'suspended' });
            });

            this.ably.connection.on('failed', (error) => {
                this.isConnected = false;
                this.connectionState = 'failed';
                // console.error('[Ably] Connection failed:', error);
                this.emit('connection', { state: 'failed', error });
            });

            this.ably.connection.on('closed', () => {
                this.isConnected = false;
                this.connectionState = 'closed';
                // console.log('[Ably] Connection closed');
                this.emit('connection', { state: 'closed' });
            });

            // Wait for initial connection
            await this._waitForConnection();

            return true;

        } catch (error) {
            // console.error('[Ably] Connection error:', error);
            throw error;
        }
    }

    /**
     * Wait for initial connection with timeout
     */
    async _waitForConnection(timeoutMs = 10000) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('[Ably] Connection timeout'));
            }, timeoutMs);

            const checkConnection = () => {
                if (this.isConnected) {
                    clearTimeout(timeout);
                    resolve();
                } else {
                    setTimeout(checkConnection, 100);
                }
            };

            checkConnection();
        });
    }

    /**
     * Register event listener
     * @param {string} event - Event name ('mm4-signal', 'connection')
     * @param {Function} callback - Callback function
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    /**
     * Remove event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function to remove
     */
    off(event, callback) {
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Emit event to all listeners
     * @param {string} event - Event name
     * @param {*} data - Event data
     */
    emit(event, data) {
        const callbacks = this.listeners.get(event) || [];
        callbacks.forEach(cb => {
            try {
                cb(data);
            } catch (error) {
                // console.error(`[Ably] Listener error for ${event}:`, error);
            }
        });
    }

    /**
     * Get connection status
     * @returns {Object} - Connection status info
     */
    getStatus() {
        return {
            isConnected: this.isConnected,
            connectionState: this.connectionState,
            stats: { ...this.stats }
        };
    }

    /**
     * Disconnect from Ably
     */
    disconnect() {
        if (this.ably) {
            this.ably.close();
            this.ably = null;
            this.channel = null;
        }
        this.isConnected = false;
        this.connectionState = 'disconnected';
        this.listeners.clear();
        // console.log('[Ably] Disconnected');
    }
}

// Export singleton instance
if (typeof window !== 'undefined') {
    window.AblyService = new AblyService();
}

export default AblyService;
